import { debounce } from 'lodash';
import { useEffect, useMemo, useState } from 'react';

/**
 * Wraps your state with a debounce update
 *
 * @param initialStateValue The value of the state you want to debounce
 * @param setStateValue Setter function of the state you want to debounce
 * @param debounceTime Debounce time in ms
 * @returns {Object} Raw and realtime state as **stateValue** and **setStateValue** that
 *    updates the initial state using debounce
 */
export const useDebouncedState = <T>(
  initialStateValue: T,
  setStateValue: (value: T) => void,
  debounceTime: number = 500
) => {

  const [stateValue, setStateValueState] = useState(initialStateValue);

  const debouncedUpdate = useMemo(
    () => debounce(setStateValue, debounceTime),
    [setStateValue, debounceTime],
  );

  useEffect(() => {
    setStateValueState(initialStateValue);
  }, [initialStateValue]);

  useEffect(() => {
    debouncedUpdate(stateValue);
  }, [debouncedUpdate, stateValue]);

  return {
    stateValue,
    setStateValue: setStateValueState,
  };
};
